import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import useAxiosHelper from "../../Common/AxiosHelper";
import { ApiPaths } from "../../Config/ApiPath";
import { BasicInfo } from "../../Config/BasicInfo";
import "../News/NewsApp.css";
import {
  Bell,
  BellOff,
  ArrowDownToLine,
  ArrowUpRight,
  Layers,
  RefreshCcw,
  TrendingUp,
  Megaphone,
  XCircle,
  CheckCircle2,
  Clock,
  ChevronRight,
  CheckCheck,
} from "lucide-react";

/* There is no notifications API, so this page builds a feed from real data:
   account activity (transactions) + published announcements. "New" means
   newer than this device's last visit (stored locally). */
const SEEN_KEY = "ax_notifications_seen_at";
const TX_LIMIT = 25;
const NEWS_LIMIT = 5;

const money = (n) =>
  `$${(parseFloat(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const plain = (s) =>
  String(s || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const readSeen = () => {
  try {
    return Number(localStorage.getItem(SEEN_KEY)) || 0;
  } catch (e) {
    return 0;
  }
};
const writeSeen = (t) => {
  try {
    localStorage.setItem(SEEN_KEY, String(t));
  } catch (e) {
    /* storage unavailable — "New" markers just won't persist */
  }
};

const NotificationPage = () => {
  const { AxiosGet } = useAxiosHelper();
  const navigate = useNavigate();
  const [tx, setTx] = useState(null);
  const [news, setNews] = useState(null);
  const [walletNames, setWalletNames] = useState({});
  const [tab, setTab] = useState("all");
  const seenAtRef = useRef(readSeen()); // last visit, captured before we update it
  const [seenAt, setSeenAt] = useState(seenAtRef.current);

  useEffect(() => {
    FetchData();
    // mark everything as seen for the next visit
    return () => writeSeen(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function FetchData() {
    const [txRes, newsRes, walletRes] = await Promise.allSettled([
      AxiosGet(`${ApiPaths.getPaymentTransaction}?${new URLSearchParams({ page: 1, limit: TX_LIMIT })}`),
      AxiosGet(`${ApiPaths.announcement}?${new URLSearchParams({ status: 1, page: 1, limit: NEWS_LIMIT })}`),
      AxiosGet(ApiPaths.getWallets),
    ]);
    setTx(txRes.status === "fulfilled" && Array.isArray(txRes.value?.data) ? txRes.value.data : []);
    setNews(newsRes.status === "fulfilled" && Array.isArray(newsRes.value?.data) ? newsRes.value.data : []);
    if (walletRes.status === "fulfilled") {
      const map = {};
      (walletRes.value?.wallets || []).forEach((w) => w?.slug && (map[w.slug] = w.name));
      setWalletNames(map);
    } else {
      BasicInfo.isDebug && console.log(walletRes.reason);
    }
  }

  // turn a transaction into a human notification
  const describeTx = (t) => {
    const src = t?.source || "";
    const amt = money(t?.amount);
    const credit = t?.debit_credit !== "debit";
    if (src === "generate_fund" || src === "add_fund")
      return { Icon: ArrowDownToLine, tone: "pos", title: "Deposit received", body: `${amt} added to your Fund Wallet.`, to: "/deposit" };
    if (src === "topup")
      return { Icon: Layers, tone: "brand", title: "Package purchased", body: `Your ${amt} package is now active.`, to: "/package" };
    if (src === "withdrawal") {
      if (Number(t?.status) === 2) return { Icon: XCircle, tone: "neg", title: "Withdrawal rejected", body: `${amt} has been returned to your wallet.`, to: "/claim" };
      if (Number(t?.status) === 1) return { Icon: CheckCircle2, tone: "pos", title: "Withdrawal completed", body: `${money(t?.withdrawal_amount || t?.amount)} sent to your payout address.`, to: "/claim" };
      return { Icon: Clock, tone: "wait", title: "Withdrawal requested", body: `${amt} is being processed.`, to: "/claim" };
    }
    if (src === "fund_convert")
      return { Icon: RefreshCcw, tone: "blue", title: "Funds converted", body: `${amt} moved to your Fund Wallet.`, to: "/fund" };
    if (/income/.test(src) && credit) {
      const name = walletNames[src] || src.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      return {
        Icon: TrendingUp,
        tone: "pos",
        title: `${name} credited`,
        body: `+${amt}${t?.to_from_username || t?.to_from ? ` from ${t.to_from_username || t.to_from}` : ""}.`,
        to: "/incomes",
        state: { incomeSlug: src },
      };
    }
    return {
      Icon: credit ? ArrowDownToLine : ArrowUpRight,
      tone: credit ? "pos" : "neg",
      title: src ? src.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Account activity",
      body: `${credit ? "+" : "−"}${amt}`,
      to: "/transaction_history",
    };
  };

  const items = useMemo(() => {
    const out = [];
    (tx || [])
      // a conversion writes a debit and a credit — show it once
      .filter((t) => !(t?.source === "fund_convert" && t?.debit_credit === "debit"))
      .forEach((t, i) => {
        const time = t?.time ? new Date(t.time).getTime() : 0;
        out.push({ key: `tx-${t?._id || i}`, kind: "account", time, ...describeTx(t) });
      });
    (news || []).forEach((n, i) => {
      const time = n?.date || n?.createdAt ? new Date(n.date || n.createdAt).getTime() : 0;
      out.push({
        key: `news-${n?._id || n?.id || i}`,
        kind: "news",
        time,
        Icon: Megaphone,
        tone: "brand",
        title: n?.title || "Announcement",
        body: plain(n?.description),
        to: "/news",
      });
    });
    return out.sort((a, b) => b.time - a.time);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tx, news, walletNames]);

  const loading = tx === null || news === null;
  const shown = tab === "all" ? items : items.filter((x) => x.kind === tab);
  const unread = items.filter((x) => x.time > seenAt).length;

  // group by day
  const groups = useMemo(() => {
    const g = [];
    shown.forEach((it) => {
      const m = moment(it.time);
      const key = m.format("YYYY-MM-DD");
      const label = m.isSame(moment(), "day") ? "Today" : m.isSame(moment().subtract(1, "day"), "day") ? "Yesterday" : m.format("ddd, DD MMM");
      const last = g[g.length - 1];
      if (last && last.key === key) last.items.push(it);
      else g.push({ key, label, items: [it] });
    });
    return g;
  }, [shown]);

  const markAllRead = () => {
    const now = Date.now();
    writeSeen(now);
    setSeenAt(now);
  };

  return (
    <section className="ntPage">
      <div className="ntHead">
        <div>
          <h1>Notifications</h1>
          <p>{unread > 0 ? `${unread} new since your last visit` : "You're all caught up"}</p>
        </div>
        {unread > 0 && (
          <button type="button" className="ntMarkRead" onClick={markAllRead}>
            <CheckCheck size={15} /> Mark all read
          </button>
        )}
      </div>

      <div className="ntTabs" role="tablist">
        {[
          { key: "all", label: "All" },
          { key: "account", label: "Account" },
          { key: "news", label: "Announcements" },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={tab === t.key ? "isOn" : ""}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="ntCard">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="ntItem nwSkel">
              <span className="thumb round" />
              <span className="lines">
                <span className="a" />
                <span className="b" />
              </span>
            </div>
          ))}
        </div>
      ) : shown.length === 0 ? (
        <div className="nwEmpty">
          <span>
            <BellOff size={26} />
          </span>
          <b>No notifications yet</b>
          <small>Deposits, purchases, withdrawals, income and announcements will show up here.</small>
        </div>
      ) : (
        groups.map((g) => (
          <div key={g.key} className="ntGroup">
            <div className="ntGroupHead">{g.label}</div>
            <div className="ntCard">
              {g.items.map((it) => {
                const isNew = it.time > seenAt;
                const Icon = it.Icon;
                return (
                  <button
                    key={it.key}
                    type="button"
                    className={`ntItem ${isNew ? "isNew" : ""}`}
                    onClick={() => navigate(it.to, it.state ? { state: it.state } : undefined)}
                  >
                    <span className={`ntIcon ${it.tone}`}>
                      <Icon size={18} />
                    </span>
                    <span className="ntText">
                      <b>{it.title}</b>
                      <small>{it.body}</small>
                      <em>{moment(it.time).fromNow()}</em>
                    </span>
                    {isNew ? <i className="ntDot" aria-label="New" /> : <ChevronRight size={16} className="nwChev" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}

      {!loading && items.length > 0 && (
        <p className="ntFoot">
          <Bell size={12} /> Showing your latest activity and announcements
        </p>
      )}
    </section>
  );
};

export default NotificationPage;
